import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Printer, Download, RefreshCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const EarningLeaveFormPrint = () => {
  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const branchId = qs.get("branchId") || "Select";
  const year = qs.get("year") || "Select";
  const empCode = qs.get("empCode") || "";
  const empName = qs.get("empName") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageTitle = useMemo(() => {
    if (!year || year === "Select") return "Earn Leave Report";
    return `Earn Leave Report (${year} - ${Number(year) + 1})`;
  }, [year]);

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing branchId / branchType in localStorage.");
      return;
    }
    if (!year || year === "Select") {
      toast.warn("Year is required.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/earning-leave-form-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          branchId,
          branchType,
          loginBranchId,
          year,
          empCode: empCode.trim(),
          empName: empName.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
      } else {
        toast.error(res?.data?.message || "Failed to load print data");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  const handleExcel = () => {
    if (!year || year === "Select") {
      toast.warn("Please Select Year");
      return;
    }

    const q = new URLSearchParams({
      finYearId: String(finYearId),
      branchId: String(branchId),
      branchType: String(branchType),
      loginBranchId: String(loginBranchId),
      year: String(year),
      empCode: empCode.trim(),
      empName: empName.trim(),
    }).toString();

    window.location.href = `${BASE_URL}/api/earning-leave-form-report/excel?${q}`;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={2200} />

      {/* ✅ PRINT + FIT CSS */}
      <style>{`
        /* Screen layout */
        .print-page {
          max-width: 1100px;
          margin: 0 auto;
          padding: 18px;
        }
        .top-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-bottom: 14px;
        }
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 12px;
          border: 1px solid #ddd;
          background: #fff;
          cursor: pointer;
          font-weight: 700;
        }
        .btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-primary {
          background: #16a34a;
          border-color: #16a34a;
          color: #fff;
        }

        /* Print area */
        .print-area {
          border: 2px solid #000;
          padding: 10px;
        }

        .print-header {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .logo-box {
          width: 110px;
          height: 95px;
          border: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #fff;
        }
        .logo-box img { width: 100%; height: 100%; object-fit: contain; }

        .header-center {
          flex: 1;
          text-align: center;
          line-height: 1.2;
        }
        .header-right {
          width: 220px;
          text-align: right;
          font-size: 12px;
          line-height: 1.3;
        }

        .title {
          text-align: center;
          margin: 12px 0 8px;
          font-weight: 800;
          font-size: 18px;
        }

        .table-wrap {
          margin-top: 6px;
          overflow: auto; /* screen only (print will override) */
        }

        table.print-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #000;
        }
        .print-table th, .print-table td {
          border: 1px solid #000;
          padding: 6px;
          font-size: 12px;
          text-align: center;
          vertical-align: middle;
        }
        .print-table td.left { text-align: left; }

        .printed-on {
          margin-top: 10px;
          text-align: right;
          font-size: 12px;
        }

        /* ✅ PRINT RULES */
        @media print {
          .top-actions, .no-print { display: none !important; }

          /* remove any scrollbars in print */
          html, body { overflow: visible !important; background: #fff !important; }
          .print-page { max-width: none !important; margin: 0 !important; padding: 0 !important; }
          .table-wrap { overflow: visible !important; }

          /* fit table to page */
          table.print-table {
            table-layout: fixed !important;
            width: 100% !important;
          }
          .print-table th, .print-table td {
            font-size: 10px !important;
            padding: 4px !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
          }

          thead { display: table-header-group; }  /* repeat header */
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid !important; }

          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>

      <div className="print-page">
        {/* Buttons (screen only) */}
        <div className="top-actions no-print">
          <button className="btn" onClick={fetchPrintData} disabled={loading}>
            <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>

          <button className="btn" onClick={handleExcel} disabled={loading}>
            <Download size={16} />
            Download Excel
          </button>

          <button className="btn btn-primary" onClick={handlePrint} disabled={loading}>
            <Printer size={16} />
            Print
          </button>
        </div>

        {/* Print Template Only */}
        <div className="print-area">
          <div className="print-header">
            <div className="logo-box">
              {/* ✅ Put file in: public/assets/nissi-logo.png */}
              <img src="/assets/nissi-logo.png" alt="Nissi Logo" />
            </div>

            <div className="header-center">
              <div style={{ fontSize: 20, fontWeight: 900 }}>Nissi Infotech</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Nissi</div>
              <div style={{ fontSize: 14, fontWeight: 900, marginTop: 4 }}>CIVIL CONSTRUCTION</div>
              <div style={{ fontSize: 12, marginTop: 8 }}>
                3/42-12, Nagappa colony, kavundampalayam, Tamil Nadu, India.
              </div>
              <div style={{ fontSize: 12 }}>
                Phone No : 9003744644 &nbsp; | &nbsp; infrasoft-implementation@nissiinfotech.in
              </div>
            </div>

            <div className="header-right">
              <div style={{ fontWeight: 800 }}>Earning Leave Form</div>
              <div>
                Year:&nbsp;
                <b>{year}</b>
                {year !== "Select" ? ` to ${Number(year) + 1}` : ""}
              </div>
              <div>
                Emp Code : <b>{empCode || "-"}</b>
              </div>
              <div>
                Emp Name : <b>{empName || "-"}</b>
              </div>
            </div>
          </div>

          <div className="title">{pageTitle}</div>

          <div className="table-wrap">
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>S.No</th>
                  <th style={{ width: 55 }}>Emp Code</th>
                  <th style={{ width: 90 }}>Emp Name</th>
                  <th style={{ width: 110 }}>Designation</th>
                  <th style={{ width: 40 }}>OB</th>
                  {["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"].map((m) => (
                    <th key={m} style={{ width: 35 }}>{m}</th>
                  ))}
                  <th style={{ width: 45 }}>BL</th>
                  <th style={{ width: 70 }}>Lapsed Leave</th>
                  <th style={{ width: 95 }}>Location</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={1 + 4 + 1 + 12 + 3}>Loading...</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={1 + 4 + 1 + 12 + 3}>No Records Found</td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={(r.fld_employeeid ?? idx) + "-" + idx}>
                      <td>{idx + 1}</td>
                      <td>{r.fld_employeecode ?? r.fld_Employeecode ?? ""}</td>
                      <td className="left">{r.fld_employee ?? r.fld_Employee ?? ""}</td>
                      <td className="left">{r.fld_designationname ?? ""}</td>
                      <td>{r.OB ?? ""}</td>

                      <td>{r.April ?? ""}</td>
                      <td>{r.May ?? ""}</td>
                      <td>{r.June ?? ""}</td>
                      <td>{r.July ?? ""}</td>
                      <td>{r.August ?? ""}</td>
                      <td>{r.September ?? ""}</td>
                      <td>{r.October ?? ""}</td>
                      <td>{r.November ?? ""}</td>
                      <td>{r.December ?? ""}</td>
                      <td>{r.January ?? ""}</td>
                      <td>{r.February ?? ""}</td>
                      <td>{r.March ?? ""}</td>

                      <td><b>{r.Bal ?? ""}</b></td>
                      <td>{r.LapsedLeave ?? ""}</td>
                      <td>{r.fld_branchname ?? ""}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="printed-on">
            Printed on: <b>{new Date().toLocaleString()}</b>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningLeaveFormPrint;
